import { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { FiAlertTriangle, FiCheckCircle, FiCopy, FiFileText, FiKey } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Button } from '../ui/Button';

export const ExcelPasswordModal = ({ exportInfo, onClose, t }) => {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  useEffect(() => {
    setCopied(false);
    setCopyFailed(false);
  }, [exportInfo]);

  const copyPassword = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(exportInfo.password);
      setCopied(true);
      setCopyFailed(false);
      toast.success(t('Excel password copied'));
    } catch (_error) {
      setCopyFailed(true);
      toast.error(t('Unable to copy'));
    }
  };

  const close = () => {
    if (copied) onClose();
  };

  return <Modal
    open={Boolean(exportInfo)}
    closable={false}
    footer={null}
    centered
    width={590}
    className='excel-password-modal'
    destroyOnClose
    maskClosable={false}
    keyboard={false}
  >
    <section aria-labelledby='excel-password-title' className='overflow-hidden rounded-[var(--radius-lg)]'>
      <header className='flex items-start gap-4 border-b border-[var(--border-subtle)] bg-[var(--surface-raised)] px-5 py-5 sm:px-6'>
        <span className='flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-[var(--brand-soft)] text-[var(--brand)]'>
          <FiKey className='h-6 w-6' aria-hidden='true' />
        </span>
        <div>
          <h2 id='excel-password-title' className='text-xl font-bold text-[var(--text-primary)]'>{t('Your Excel password')}</h2>
          <p className='mt-1 text-sm text-[var(--text-secondary)]'>{t('This password is shown only once. Copy it before closing this window.')}</p>
        </div>
      </header>

      <div className='space-y-5 px-5 py-5 sm:px-6'>
        <div className='flex items-center gap-3 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-panel-muted)] p-3 text-sm text-[var(--text-secondary)]'>
          <FiFileText className='shrink-0 text-[var(--brand)]' aria-hidden='true' />
          <span className='min-w-0 break-all font-medium text-[var(--text-primary)]'>{exportInfo?.filename}</span>
        </div>

        <div>
          <label htmlFor='excel-export-password' className='mb-2 block text-sm font-bold text-[var(--text-primary)]'>{t('Password to open the file')}</label>
          <input
            id='excel-export-password'
            readOnly
            value={exportInfo?.password || ''}
            onFocus={(event) => event.target.select()}
            className='w-full rounded-[var(--radius-md)] border border-[var(--brand)]/50 bg-[var(--surface-panel)] px-4 py-4 text-center font-mono text-lg font-bold tracking-wider text-[var(--text-primary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/25 sm:text-xl'
          />
        </div>

        <div className='flex gap-3 rounded-[var(--radius-md)] border border-amber-500/35 bg-amber-500/10 p-4 text-sm text-[var(--text-secondary)]'>
          <FiAlertTriangle className='mt-0.5 shrink-0 text-amber-500' aria-hidden='true' />
          <p>{t('After closing this window, the password cannot be recovered from the application.')}</p>
        </div>

        <Button variant={copied ? 'secondary' : 'primary'} className='w-full' onClick={copyPassword} disabled={copied}>
          {copied ? <FiCheckCircle aria-hidden='true' /> : <FiCopy aria-hidden='true' />}
          {t(copied ? 'Password copied' : 'Copy password')}
        </Button>

        {copyFailed && <div className='rounded-[var(--radius-md)] border border-[var(--border-subtle)] p-4 text-sm text-[var(--text-secondary)]'>
          <p>{t('Your browser blocked automatic copying. Select and copy the password manually, then confirm that you saved it.')}</p>
          <Button className='mt-3 w-full' onClick={() => setCopied(true)}>{t('I saved the password')}</Button>
        </div>}

        <div className='border-t border-[var(--border-subtle)] pt-4'>
          <Button className='w-full sm:ml-auto sm:flex sm:w-auto' variant='secondary' disabled={!copied} onClick={close}>{t('Close')}</Button>
          {!copied && <p className='mt-2 text-center text-xs text-[var(--text-muted)] sm:text-right'>{t('Copy or save the password to enable closing.')}</p>}
        </div>
      </div>
    </section>
  </Modal>;
};
